import { useEffect, useRef } from 'react';
import { environment } from '@/config/environment';
import { useAuth } from '@/hooks/useAuth';
import { useBranding } from '@/hooks/useBranding';
import { useUnreadNotificationCountQuery } from '@/hooks/useNotifications';

function buildPrefixedTitle(unreadCount: number, baseTitle: string): string {
  if (unreadCount <= 0) {
    return baseTitle;
  }
  return '(' + String(unreadCount) + ') ' + baseTitle;
}

export function useNotificationTabTitle(): void {
  const unreadCountQuery = useUnreadNotificationCountQuery();
  const unreadCount = unreadCountQuery.data === undefined ? 0 : unreadCountQuery.data;
  const { status, activeScopeCode } = useAuth();
  const isInWorkspaceOrPortalContext =
    status === 'authenticated' && (activeScopeCode === 'tenant' || activeScopeCode === 'client');
  const { brandName } = useBranding();
  const baseTitleRef = useRef<string | null>(null);
  const wasInWorkspaceOrPortalContextRef = useRef(false);

  useEffect(function captureAndRestoreBaseTitle() {
    baseTitleRef.current = document.title;
    return function restoreBaseTitle() {
      const baseTitle = baseTitleRef.current;
      if (baseTitle !== null) {
        document.title = baseTitle;
      }
    };
  }, []);

  useEffect(
    function applyWorkspaceBrandNameAsBaseTitle() {
      if (isInWorkspaceOrPortalContext) {
        baseTitleRef.current = brandName;
        wasInWorkspaceOrPortalContextRef.current = true;
        return;
      }
      if (wasInWorkspaceOrPortalContextRef.current) {
        baseTitleRef.current = environment.brandName;
        wasInWorkspaceOrPortalContextRef.current = false;
      }
    },
    [isInWorkspaceOrPortalContext, brandName]
  );

  useEffect(
    function applyUnreadPrefix() {
      const baseTitle = baseTitleRef.current;
      if (baseTitle === null) {
        return;
      }
      document.title = buildPrefixedTitle(unreadCount, baseTitle);
    },
    [unreadCount, isInWorkspaceOrPortalContext, brandName]
  );
}
